import { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { getPublicSiteUrl } from '@/lib/domain'
import type { Vehicle } from '@limo/database'
import { type QuoteStatus, applyQuoteDiscount, type QuoteDiscount } from '@limo/shared'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import {
  ArrowLeft,
  AlertCircle,
  Car,
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  Users,
  Copy,
  ExternalLink,
  FileText,
  Tag,
  Globe,
  ArrowLeftRight,
  Percent,
  Wallet,
} from 'lucide-react'
import { PricingAuditCard } from '@/components/bookings'
import { ApplyDiscountDialog } from '@/components/discounts'
import { DepositModeControl } from '@/components/deposits'
import type { PaymentMode } from '@limo/shared/pricing'
import { GHLSyncLogsSection } from '@/components/integrations/GHLSyncLogsSection'
import { resolveStoredClickId, summarizeLandingUrl } from '@/lib/attribution/display'

// Quote type from database
interface QuoteDetail {
  id: string
  token: string
  account_id: string
  status: QuoteStatus
  service_type: string
  pickup_address: string
  pickup_lat: number | null
  pickup_lng: number | null
  dropoff_address: string | null
  dropoff_lat: number | null
  dropoff_lng: number | null
  pickup_datetime: string
  return_datetime: string | null
  return_trip: boolean
  duration_hours: number | null
  customer_name: string | null
  customer_email: string | null
  customer_phone: string | null
  passenger_count: number | null
  special_requests: string | null
  vehicle_id: string | null
  pricing_breakdown: Array<{ label: string; amount: number }> | null
  /**
   * Round trips only: per-leg pricing split from calculate-price. The return
   * leg's total already includes the return-trip discount, so the pair sums
   * to `total_amount`. Null for one-way quotes and legacy round trips.
   */
  leg_allocation: {
    outbound: { total: number }
    return: { total: number }
  } | null
  subtotal: number | null
  gratuity_percent: number | null
  gratuity_amount: number | null
  taxes: number | null
  total_amount: number | null
  discount_type: 'percent' | 'flat' | null
  discount_value: number | null
  discount_source: string | null
  /** Operator override of the account payment mode. null = inherit. */
  deposit_mode_override: PaymentMode | null
  distance_miles: number | null
  estimated_duration_minutes: number | null
  is_flat_rate: boolean
  flat_rate_name: string | null
  is_airport_pickup: boolean
  is_airport_dropoff: boolean
  visitor_id: string | null
  session_id: string | null
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_term: string | null
  utm_content: string | null
  landing_url: string | null
  gclid: string | null
  created_at: string
  updated_at: string
  expires_at: string
  converted_at: string | null
  booking_id: string | null
  pricing_audit: Record<string, unknown> | null
  is_operator_priced: boolean
  operator_token: string | null
  operator_price_note: string | null
  priced_at: string | null
  operator_notified_at: string | null
  vehicles: Pick<Vehicle, 'id' | 'name'> | null
}

// Status badge variants
const quoteStatusStyles: Record<
  QuoteStatus,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }
> = {
  trip_details: { variant: 'secondary', label: 'Trip Details' },
  vehicle_selected: { variant: 'default', label: 'Vehicle Selected' },
  contact_provided: { variant: 'default', label: 'Contact Provided' },
  quote_requested: { variant: 'destructive', label: 'Quote Requested' },
  priced: { variant: 'default', label: 'Priced' },
  converted: { variant: 'outline', label: 'Converted' },
  expired: { variant: 'destructive', label: 'Expired' },
}

// Format helpers
const formatCurrency = (amount: number | null) =>
  amount !== null
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(amount)
    : '—'

const formatDateTime = (dateStr: string, timezone?: string | null) =>
  new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
  })

const formatServiceType = (
  type: string,
  isAirportPickup?: boolean | null,
  isAirportDropoff?: boolean | null,
) => {
  if ((isAirportPickup || isAirportDropoff) && type === 'point_to_point') {
    return 'Airport Transportation'
  }
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const queryClient = useQueryClient()
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false)

  const activeAccount = isImpersonating ? impersonatedAccount : account

  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  // Fetch quote
  const {
    data: quote,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['quote-detail', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('booking_quotes')
        .select(
          `
          *,
          vehicles (id, name)
        `
        )
        .eq('id', id)
        // Soft-deleted quotes are hidden — a stale URL reads as not found
        .is('deleted_at', null)
        .single()
      if (error) throw error
      // `as unknown as` because QuoteDetail is a hand-written shape that also
      // carries the `vehicles` join, which the generated Row type doesn't model.
      return data as unknown as QuoteDetail
    },
    enabled: !!id,
  })

  // The account's own payment mode, so the override control can name what
  // "inherit" currently resolves to rather than making the operator look it up.
  const { data: accountPaymentSettings } = useQuery({
    queryKey: ['account-payment-settings', quote?.account_id],
    queryFn: async () => {
      if (!quote?.account_id) return {}
      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', quote.account_id)
        .in('key', ['booking_payment_mode', 'booking_require_deposit'])
      if (error) throw error
      return {
        paymentMode: data?.find((s) => s.key === 'booking_payment_mode')?.value,
        requireDeposit: data?.find((s) => s.key === 'booking_require_deposit')?.value,
      }
    },
    enabled: !!quote?.account_id,
  })

  // Override the payment mode for this quote alone. Checkout reads this column
  // server-authoritatively in create-payment-intent, so what is set here is what
  // the customer is actually charged — the client never sends an amount.
  const depositModeMutation = useMutation({
    mutationFn: async (mode: PaymentMode | null) => {
      if (!id) throw new Error('Quote ID is required')
      const { error } = await supabase
        .from('booking_quotes')
        .update({ deposit_mode_override: mode } as never)
        .eq('id', id)
      if (error) throw error
      return mode
    },
    onSuccess: (mode) => {
      queryClient.invalidateQueries({ queryKey: ['quote-detail', id] })
      toast({
        title: mode ? 'Payment option updated' : 'Following the account default',
        description: mode
          ? 'This customer will see the option you picked when they check out.'
          : 'This quote now uses whatever the account is set to.',
      })
    },
    onError: (mutationError) => {
      toast({
        title: 'Error updating the payment option',
        description: mutationError.message,
        variant: 'destructive',
      })
    },
  })

  // Apply or clear the quote's discount. Writes the same columns the GoHighLevel
  // apply-quote-discount webhook writes, so there is one active discount per quote
  // and checkout needs no changes — it already reads these fields
  // server-authoritatively in create-payment-intent.
  const discountMutation = useMutation({
    mutationFn: async (discount: QuoteDiscount | null) => {
      if (!id) throw new Error('Quote ID is required')

      const { error } = await supabase
        .from('booking_quotes')
        .update(
          discount
            ? {
                discount_type: discount.type,
                discount_value: discount.value,
                discount_source: 'builder',
                discount_applied_at: new Date().toISOString(),
              }
            : {
                discount_type: null,
                discount_value: null,
                discount_source: null,
                discount_applied_at: null,
              }
        )
        .eq('id', id)

      if (error) throw error
      return discount
    },
    onSuccess: (discount) => {
      queryClient.invalidateQueries({ queryKey: ['quote-detail', id] })
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      setDiscountDialogOpen(false)
      toast({
        title: discount ? 'Discount applied' : 'Discount removed',
        description: discount
          ? 'The customer will see the discounted total when they open this quote.'
          : 'This quote is back to its full price.',
      })
    },
    onError: (mutationError) => {
      toast({
        title: 'Error saving discount',
        description: mutationError.message,
        variant: 'destructive',
      })
    },
  })

  // Get resume URL for quote
  const getResumeUrl = () => {
    if (!quote || !activeAccount) return ''
    const baseUrl = activeAccount.custom_domain
      ? `https://${activeAccount.custom_domain}`
      : getPublicSiteUrl(activeAccount.slug)
    return `${baseUrl}/quote/${quote.token}`
  }

  // Copy link to clipboard
  const handleCopyLink = () => {
    const url = getResumeUrl()
    navigator.clipboard.writeText(url)
    toast({
      title: 'Link copied',
      description: 'Quote resume link has been copied to clipboard.',
    })
  }

  // Set-price URL for operator-priced quotes (fallback when the GHL
  // notification didn't reach the operator)
  const getSetPriceUrl = () => {
    if (!quote?.operator_token || !activeAccount) return ''
    const baseUrl = activeAccount.custom_domain
      ? `https://${activeAccount.custom_domain}`
      : getPublicSiteUrl(activeAccount.slug)
    return `${baseUrl}/quote/set-price/${quote.operator_token}`
  }

  const handleCopySetPriceLink = () => {
    const url = getSetPriceUrl()
    navigator.clipboard.writeText(url)
    toast({
      title: 'Link copied',
      description: 'Set-price link has been copied to clipboard.',
    })
  }

  // Check if quote can be shared (not converted or expired)
  const canShare = quote && !['converted', 'expired'].includes(quote.status)

  // Check if quote is expired (based on time, not status)
  const isExpired = quote && new Date(quote.expires_at) < new Date() && quote.status !== 'converted'

  // Calculate time remaining
  const getTimeRemaining = () => {
    if (!quote) return ''
    const now = new Date()
    const expires = new Date(quote.expires_at)
    const diff = expires.getTime() - now.getTime()

    if (diff <= 0) return 'Expired'

    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`
    return `${hours} hour${hours > 1 ? 's' : ''} remaining`
  }

  if (!activeAccount || !id) {
    return null
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <Skeleton className="h-10 w-10" />
            <div>
              <Skeleton className="h-8 w-48" />
              <div className="mt-2 flex gap-2">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-16" />
              </div>
            </div>
          </div>
          <Skeleton className="h-10 w-24" />
        </div>

        {/* Content skeleton */}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (error || !quote) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-lg font-medium mb-2">Quote not found</p>
        <p className="text-muted-foreground mb-4">
          The quote you&apos;re looking for doesn&apos;t exist or you don&apos;t have access to it.
        </p>
        <Button variant="outline" onClick={() => navigate(`${basePath}/quotes`)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Quotes
        </Button>
      </div>
    )
  }

  // Parse pricing breakdown from JSONB
  const pricingBreakdown = Array.isArray(quote.pricing_breakdown)
    ? quote.pricing_breakdown
    : []

  // Discount attached to this quote — set here in the Builder or by a GoHighLevel
  // workflow via the apply-quote-discount webhook. One active discount either way;
  // `discount_source` records which set it. Applied at checkout.
  const quoteDiscount =
    (quote.discount_type === 'percent' || quote.discount_type === 'flat') &&
    quote.discount_value != null &&
    Number(quote.discount_value) > 0
      ? { type: quote.discount_type, value: Number(quote.discount_value) }
      : null

  // Preview the discounted total using the SAME helper the checkout uses, so the
  // net figure here matches what the customer is charged. Null until the quote
  // has a price (no vehicle selected yet).
  // Only the total drives the discount math here; the returned breakdown isn't
  // rendered on this page, so an empty breakdown keeps the call type-clean.
  const discountedResult =
    quoteDiscount && quote.total_amount != null
      ? applyQuoteDiscount(
          { total: Number(quote.total_amount), breakdown: [] },
          quoteDiscount
        )
      : null

  const discountBadgeLabel = quoteDiscount
    ? quoteDiscount.type === 'percent'
      ? `${quoteDiscount.value}% Discount`
      : `${formatCurrency(quoteDiscount.value)} Discount`
    : null

  // Where the discount came from, so an operator can tell a rep's GHL discount
  // apart from one applied here or via the tokenized discount link.
  const discountSourceLabel =
    quote.discount_source === 'ghl'
      ? 'via GoHighLevel'
      : quote.discount_source === 'builder'
        ? 'applied here'
        : quote.discount_source === 'operator_link'
          ? 'via discount link'
          : null

  // A converted quote is a paid booking — its price can no longer change (the
  // webhook rejects these with 409, and so do we). Discount that booking instead.
  const canEditDiscount = quote.status !== 'converted'

  // Check if there are any UTM params
  // A landing URL or click ID alone is worth showing: a paid click can arrive
  // with no UTM tags at all, and it's the click ID that ties the quote to an ad.
  const hasUtmParams = quote.utm_source || quote.utm_medium || quote.utm_campaign || quote.utm_term || quote.utm_content
  const clickId = resolveStoredClickId(quote)
  const landing = summarizeLandingUrl(quote.landing_url)
  const hasAdAttribution = hasUtmParams || Boolean(clickId) || Boolean(landing)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`${basePath}/quotes`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">
                Quote Details
              </h1>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge
                variant={
                  quoteStatusStyles[quote.status as QuoteStatus]?.variant || 'secondary'
                }
              >
                {quoteStatusStyles[quote.status as QuoteStatus]?.label ||
                  quote.status}
              </Badge>
              {isExpired && quote.status !== 'expired' && (
                <Badge variant="destructive">Expired</Badge>
              )}
              {quote.is_flat_rate && (
                <Badge variant="outline">Flat Rate</Badge>
              )}
              {discountBadgeLabel && (
                <Badge
                  variant="outline"
                  className="border-green-300 bg-green-50 text-green-700"
                >
                  <Percent className="mr-1 h-3 w-3" />
                  {discountBadgeLabel}
                  {discountSourceLabel && (
                    <span className="ml-1 font-normal text-green-600">
                      · {discountSourceLabel}
                    </span>
                  )}
                </Badge>
              )}
              <span className="text-sm text-muted-foreground">
                Created {formatDateTime(quote.created_at)}
              </span>
            </div>
            {quote.status !== 'converted' && (
              <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {getTimeRemaining()}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {canEditDiscount && (
            <Button variant="outline" onClick={() => setDiscountDialogOpen(true)}>
              <Percent className="mr-2 h-4 w-4" />
              {quoteDiscount ? 'Edit Discount' : 'Apply Discount'}
            </Button>
          )}
          {canShare && quote.is_operator_priced && quote.operator_token && (
            <Button variant="outline" onClick={handleCopySetPriceLink}>
              <Tag className="mr-2 h-4 w-4" />
              Copy Set-Price Link
            </Button>
          )}
          {canShare && (
            <>
              <Button variant="outline" onClick={handleCopyLink}>
                <Copy className="mr-2 h-4 w-4" />
                Copy Link
              </Button>
              <Button variant="outline" asChild>
                <a href={getResumeUrl()} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open Quote
                </a>
              </Button>
            </>
          )}
          {quote.booking_id && (
            <Button variant="default" asChild>
              <Link to={`${basePath}/bookings/${quote.booking_id}`}>
                View Booking
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Quote request awaiting a price */}
      {quote.status === 'quote_requested' && (
        <div className="flex items-center gap-2 rounded-lg bg-orange-50 border border-orange-200 px-4 py-3">
          <Clock className="h-4 w-4 text-orange-600 flex-shrink-0" />
          <p className="text-sm font-medium text-orange-700">
            This customer is waiting for a price. Use the &quot;Copy Set-Price Link&quot;
            button to open the pricing page and send them a quote.
          </p>
        </div>
      )}

      {/* Operator price note */}
      {quote.is_operator_priced && quote.operator_price_note && (
        <div className="flex items-center gap-2 rounded-lg bg-teal-50 border border-teal-200 px-4 py-3">
          <Tag className="h-4 w-4 text-teal-600 flex-shrink-0" />
          <p className="text-sm text-teal-700">
            <span className="font-medium">Your pricing note:</span> {quote.operator_price_note}
          </p>
        </div>
      )}

      {/* Round Trip Banner */}
      {quote.return_trip && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3">
          <ArrowLeftRight className="h-4 w-4 text-green-600 flex-shrink-0" />
          <p className="text-sm font-medium text-green-700">
            Round Trip — Price includes both outbound and return legs.
          </p>
        </div>
      )}

      {/* Main content grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Trip Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              Trip Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 mt-1 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Service Type</p>
                  <p className="font-medium">{formatServiceType(quote.service_type, quote.is_airport_pickup, quote.is_airport_dropoff)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 mt-1 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Pickup Date & Time</p>
                  <p className="font-medium">{formatDateTime(quote.pickup_datetime, activeAccount?.timezone)}</p>
                </div>
              </div>
              {quote.return_trip && quote.return_datetime && (
                <div className="flex items-start gap-3">
                  <ArrowLeftRight className="h-4 w-4 mt-1 text-green-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Return Date & Time</p>
                    <p className="font-medium">{formatDateTime(quote.return_datetime, activeAccount?.timezone)}</p>
                  </div>
                </div>
              )}
              {quote.duration_hours && (
                <div className="flex items-start gap-3">
                  <Clock className="h-4 w-4 mt-1 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Duration</p>
                    <p className="font-medium">{quote.duration_hours} hours</p>
                  </div>
                </div>
              )}
              {quote.distance_miles && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-1 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Distance</p>
                    <p className="font-medium">{quote.distance_miles.toFixed(1)} miles</p>
                  </div>
                </div>
              )}
            </div>

            {/* Locations */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-1 text-green-600" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    Pickup {quote.is_airport_pickup && '(Airport)'}
                  </p>
                  <p className="font-medium">{quote.pickup_address}</p>
                </div>
              </div>
              {quote.dropoff_address && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-1 text-red-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Dropoff {quote.is_airport_dropoff && '(Airport)'}
                    </p>
                    <p className="font-medium">{quote.dropoff_address}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Vehicle (if selected) */}
            {quote.vehicles && (
              <div className="border-t pt-4">
                <div className="flex items-start gap-3">
                  <Car className="h-4 w-4 mt-1 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Selected Vehicle</p>
                    <p className="font-medium">{quote.vehicles.name}</p>
                    {quote.is_flat_rate && quote.flat_rate_name && (
                      <p className="text-xs text-muted-foreground">
                        Flat rate: {quote.flat_rate_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Customer Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {quote.customer_name || quote.customer_email || quote.customer_phone ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {quote.customer_name && (
                  <div className="flex items-start gap-3">
                    <User className="h-4 w-4 mt-1 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium">{quote.customer_name}</p>
                    </div>
                  </div>
                )}
                {quote.customer_email && (
                  <div className="flex items-start gap-3">
                    <Mail className="h-4 w-4 mt-1 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <a
                        href={`mailto:${quote.customer_email}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {quote.customer_email}
                      </a>
                    </div>
                  </div>
                )}
                {quote.customer_phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="h-4 w-4 mt-1 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <a
                        href={`tel:${quote.customer_phone}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {quote.customer_phone}
                      </a>
                    </div>
                  </div>
                )}
                {quote.passenger_count && (
                  <div className="flex items-start gap-3">
                    <Users className="h-4 w-4 mt-1 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Passengers</p>
                      <p className="font-medium">{quote.passenger_count}</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">
                No customer information provided yet.
              </p>
            )}

            {/* Special requests */}
            {quote.special_requests && (
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">Special Requests</p>
                <div className="rounded-lg bg-muted p-3">
                  <p className="text-sm whitespace-pre-wrap">{quote.special_requests}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* GoHighLevel Sync History */}
        <GHLSyncLogsSection quoteId={quote.id} />

        {/* Pricing (if available) */}
        {quote.total_amount && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Pricing
                {quote.return_trip && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <ArrowLeftRight className="h-3 w-3" />
                    Round Trip
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {pricingBreakdown.length > 0 && (
                <div className="space-y-2">
                  {pricingBreakdown.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{item.label}</span>
                      <span>{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Per-leg totals for round trips priced leg by leg. The return
                  leg already includes the return-trip discount. */}
              {quote.return_trip && quote.leg_allocation && (
                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Outbound leg</span>
                    <span>{formatCurrency(quote.leg_allocation.outbound.total)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Return leg</span>
                    <span>{formatCurrency(quote.leg_allocation.return.total)}</span>
                  </div>
                </div>
              )}

              <div className="border-t pt-3 space-y-2">
                {quote.subtotal && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(quote.subtotal)}</span>
                  </div>
                )}
                {quote.gratuity_amount && quote.gratuity_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Gratuity ({quote.gratuity_percent}%)
                    </span>
                    <span>{formatCurrency(quote.gratuity_amount)}</span>
                  </div>
                )}
                {quote.taxes && quote.taxes > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Taxes & Fees</span>
                    <span>{formatCurrency(quote.taxes)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium pt-2 border-t">
                  <span>Total</span>
                  <span className="text-lg">{formatCurrency(quote.total_amount)}</span>
                </div>

                {/* Discount preview — the customer pays the discounted total. */}
                {discountedResult?.ok && (
                  <>
                    <div className="flex justify-between text-sm text-green-700">
                      <span>
                        {discountedResult.label}
                        {discountSourceLabel ? ` (${discountSourceLabel})` : ''}
                      </span>
                      <span>-{formatCurrency(discountedResult.discountAmount)}</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-2 border-t text-green-700">
                      <span>Discounted Total</span>
                      <span className="text-lg">
                        {formatCurrency(discountedResult.pricing.total)}
                      </span>
                    </div>
                  </>
                )}

                {/* Discount can't apply — mirrors the checkout reject. */}
                {discountedResult && !discountedResult.ok && (
                  <div className="flex items-start gap-1.5 text-xs text-red-600 pt-1">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      {discountBadgeLabel} exceeds the trip total — it will not be
                      applied at checkout.
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Deposit override. Sits with the money, and is available even on a
            quote with no price yet — the operator often decides how this
            customer pays before deciding what to charge them. */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Payment Option
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DepositModeControl
              value={quote.deposit_mode_override ?? null}
              onChange={(mode) => depositModeMutation.mutate(mode)}
              accountSettings={accountPaymentSettings ?? {}}
              // Converted quotes are settled — the same reason the discount
              // dialog refuses them. Change it on the resulting booking instead.
              disabled={quote.status === 'converted'}
              disabledReason="This quote has been paid. Change the payment option on the booking instead."
              isSaving={depositModeMutation.isPending}
            />
          </CardContent>
        </Card>

        {/* Pricing Audit Trail */}
        <PricingAuditCard pricingAudit={quote.pricing_audit as never} />

        {/* Analytics / Attribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Analytics & Attribution
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {quote.visitor_id && (
                <div>
                  <p className="text-sm text-muted-foreground">Visitor ID</p>
                  <p className="font-mono text-xs truncate">{quote.visitor_id}</p>
                </div>
              )}
              {quote.session_id && (
                <div>
                  <p className="text-sm text-muted-foreground">Session ID</p>
                  <p className="font-mono text-xs truncate">{quote.session_id}</p>
                </div>
              )}
            </div>

            {hasAdAttribution && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Ad Attribution</p>
                <div className="grid gap-2 text-sm">
                  {quote.utm_source && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Source</span>
                      <span>{quote.utm_source}</span>
                    </div>
                  )}
                  {quote.utm_medium && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Medium</span>
                      <span>{quote.utm_medium}</span>
                    </div>
                  )}
                  {quote.utm_campaign && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Campaign</span>
                      <span>{quote.utm_campaign}</span>
                    </div>
                  )}
                  {quote.utm_term && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Term</span>
                      <span>{quote.utm_term}</span>
                    </div>
                  )}
                  {quote.utm_content && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Content</span>
                      <span>{quote.utm_content}</span>
                    </div>
                  )}
                  {clickId && (
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">{clickId.type}</span>
                      <span className="break-all font-mono text-xs">{clickId.value}</span>
                    </div>
                  )}
                </div>

                {landing && (
                  <div className="mt-3 border-t pt-3">
                    <p className="text-sm text-muted-foreground mb-1">Landing page</p>
                    {/* The whole URL, verbatim — the source of truth for every
                        click ID that has no column of its own. */}
                    <p className="break-all font-mono text-xs" title={landing.full}>
                      {landing.full}
                    </p>
                  </div>
                )}
              </div>
            )}

            {!quote.visitor_id && !quote.session_id && !hasAdAttribution && (
              <p className="text-muted-foreground">
                No analytics data available for this quote.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Apply Discount. Writes the same columns as the GHL webhook, so there is
          one active discount per quote and checkout needs no changes. */}
      <ApplyDiscountDialog
        open={discountDialogOpen}
        onOpenChange={setDiscountDialogOpen}
        preDiscountTotal={quote.total_amount != null ? Number(quote.total_amount) : 0}
        currentDiscount={quoteDiscount}
        currentDiscountAmount={discountedResult?.ok ? discountedResult.discountAmount : 0}
        entityLabel="quote"
        isPending={discountMutation.isPending}
        onApply={(discount) => discountMutation.mutate(discount)}
        onClear={() => discountMutation.mutate(null)}
      />
    </div>
  )
}
